// Pure batch-save payload assembly, extracted from CategoryQuestionnaireComponent.handleSave
// so the auto-deny / pertinent-positive logic (CAPA F122) is unit-testable in isolation.
//
// This component backs BOTH the recommended Yes/No/Unknown view AND the questionnaire Search
// dialog, so this assembly runs for both surfaces.
//
// Batch groups model a one-pass review (a symptom checklist) where the siblings the provider
// leaves unticked are recorded as pertinent negatives ("No"/scale "1"). The F122 guard
// (`existing.first_response != null`) keeps that behavior for genuinely-unanswered siblings
// while never overwriting an explicit stored answer — otherwise saving a second positive chip
// in a group re-denies the first ("each one sets the others to No").

export interface BatchSaveInput {
  itemsToSave: any[]
  data: any[]
  batchGroupData: { mapping?: Record<string, any>, groups?: Record<string, any> }
}

export interface BatchSavePayload {
  batchByGroup: { [bg: string]: Array<{ name: string, value: string }> }
  standardItems: any[]
}

export function buildBatchSavePayload({ itemsToSave, data, batchGroupData }: BatchSaveInput): BatchSavePayload {
  const batchMapping = batchGroupData.mapping || {}
  const standardItems: any[] = []
  const batchByGroup: { [bg: string]: Array<{ name: string, value: string }> } = {}

  // Track which batch groups have been touched (by any item, standard or batch)
  const touchedBatchGroups = new Set<string>()
  const touchedBatchItemNames = new Set<string>()

  // Build id-to-name lookup from data (calculator id -> questionnaire name)
  const idToName: { [id: string]: string } = {}
  data.forEach((d: any) => { if (d.id && d.name) idToName[d.id] = d.name })

  itemsToSave.forEach(item => {
    const itemName = idToName[item.id] || item.id
    const bgInfo = batchMapping[itemName] || batchMapping[item.id] || batchMapping[item.questionnaire_id]
    // Batch-group items with a single answer key (chip-level click) go through the batch
    // endpoint. Items with multiple answer keys (user expanded and edited the full
    // questionnaire inline) go through the standard endpoint to preserve the complete
    // answer set (e.g., Yes + severity=3).
    const answerKeys = Object.keys(item.answer || {})
    if (bgInfo?.batch_group && answerKeys.length <= 1) {
      const bg = bgInfo.batch_group
      if (!batchByGroup[bg]) batchByGroup[bg] = []
      const dataItem = data.find((d: any) => d.id === item.id)
      const value = answerKeys.length > 0
        ? String(item.answer[answerKeys[0]]?.value || '5')
        : (dataItem?.first_response != null ? String(dataItem.first_response) : '5')
      batchByGroup[bg].push({ name: itemName, value })
      touchedBatchGroups.add(bg)
      touchedBatchItemNames.add(itemName)
    } else {
      standardItems.push(item)
      // Still track as touched so untouched siblings get auto-denied
      if (bgInfo?.batch_group) {
        touchedBatchGroups.add(bgInfo.batch_group)
        touchedBatchItemNames.add(itemName)
      }
    }
  })

  // Auto-deny untouched RECOMMENDED batch items in touched groups.
  // Non-recommended items stay Unknown — only save them if explicitly clicked.
  const batchGroups = batchGroupData.groups || {}
  const dataByName: { [name: string]: any } = {}
  data.forEach((d: any) => { if (d.name) dataByName[d.name] = d })

  touchedBatchGroups.forEach(bg => {
    const groupInfo = batchGroups[bg]
    if (!groupInfo?.items) return
    // DM-7562: exposure-dependent groups — visible unclicked items are left unchanged
    if (new Set(['ALLEVIATING', 'AGGRAVATING']).has(groupInfo?.ui_category)) return
    if (!batchByGroup[bg]) batchByGroup[bg] = []
    groupInfo.items.forEach((item: any) => {
      if (!touchedBatchItemNames.has(item.name)) {
        // Only auto-deny items that exist in the patient's data (recommended)
        const existing = dataByName[item.name]
        if (!existing) return
        // Pertinent-positive guard (CAPA F122): never let auto-deny OVERWRITE an explicit
        // stored answer. Auto-deny records pertinent negatives for the siblings the provider
        // left unticked on a review pass — but only for genuinely-unanswered ones. Without
        // this, saving a second positive chip in the group re-denies the first ("each one
        // sets the others to No"). An answered sibling carries a non-null first_response
        // ('Yes'/'No'/scale); null = never answered. Explicit changes still work — flipping
        // an answer means *touching* it, which routes through the standard save path.
        if (existing.first_response != null) return
        const componentType = item.first_component_type || 'scale'
        const denyValue = componentType === 'scale' ? '1' : 'No'
        batchByGroup[bg].push({ name: item.name, value: denyValue })
      }
    })
  })

  return { batchByGroup, standardItems }
}
